/**
 * PhoneMockup — הדמיית מסך טלפון למדריך ההתקנה
 *
 * Draws the phone screens for the install guide in markup instead of using
 * screenshots: it stays sharp on every display, is translatable, weighs
 * nothing, and doesn't need re-shooting whenever Chrome or iOS restyles a
 * menu. The mockups are decorative — every step is fully described in text
 * next to them — so the whole frame is aria-hidden and the screen reader gets
 * the words, not a pile of empty divs.
 */

import { MoreVertical, Share, Plus, ChevronRight, ChevronLeft, Star, RotateCw, Download, BookOpen, Copy, Search } from 'lucide-react'

export type StepScreen =
  | 'android-open'
  | 'android-menu-button'
  | 'android-menu'
  | 'android-confirm'
  | 'ios-open'
  | 'ios-share-button'
  | 'ios-share-sheet'
  | 'ios-confirm'
  | 'home-screen'

const APP_ICON = '/icons/icon-192.png'
const APP_NAME = 'יפתח ונגר'

/** Pulsing ring that points at the thing the user should tap. */
const HIGHLIGHT = 'ring-4 ring-primary-500/70 animate-pulse rounded-lg'
/** Same, for a bare icon — needs to be a box before a ring around it means anything. */
const HIGHLIGHT_ICON = `${HIGHLIGHT} inline-flex items-center justify-center bg-primary-50 p-1`

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-3 pt-2 pb-1 text-[9px] font-medium text-gray-700">
      <span>9:41</span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-2 w-4 rounded-sm border border-gray-500" />
      </span>
    </div>
  )
}

/** The page body behind the browser UI — grey bars standing in for content. */
function PageContent() {
  return (
    <div className="flex-1 space-y-2 bg-white px-3 py-4">
      <img src={APP_ICON} alt="" className="mx-auto h-10 w-10 rounded-lg" />
      <div className="mx-auto h-2 w-24 rounded bg-gray-200" />
      <div className="mx-auto h-2 w-16 rounded bg-gray-200" />
      <div className="mt-4 h-8 rounded-lg bg-primary-100" />
      <div className="h-2 w-full rounded bg-gray-100" />
      <div className="h-2 w-3/4 rounded bg-gray-100" />
    </div>
  )
}

function AndroidUrlBar({ host, highlight }: { host: string; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1.5">
      <div className="flex flex-1 items-center gap-1 rounded-full bg-white px-2 py-1 shadow-sm">
        <span className="text-[8px] text-gray-400">🔒</span>
        <span className="truncate text-[9px] text-gray-700">{host}</span>
      </div>
      <span className={highlight ? HIGHLIGHT_ICON : undefined}>
        <MoreVertical size={12} className="text-gray-700" />
      </span>
    </div>
  )
}

function AndroidMenu() {
  const items = [
    { icon: <Plus size={10} />, label: 'כרטיסייה חדשה' },
    { icon: <RotateCw size={10} />, label: 'רענון' },
    { icon: <Download size={10} />, label: 'התקנת אפליקציה', active: true },
    { icon: <Star size={10} />, label: 'מועדפים' },
  ]
  return (
    <div className="absolute inset-x-3 top-10 z-10 overflow-hidden rounded-lg bg-white shadow-xl">
      {items.map((item) => (
        <div
          key={item.label}
          className={`flex items-center justify-between px-2.5 py-2 text-[9px] ${
            item.active ? `bg-primary-50 font-semibold text-primary-700 ${HIGHLIGHT}` : 'text-gray-700'
          }`}
        >
          <span>{item.label}</span>
          <span className="text-gray-400">{item.icon}</span>
        </div>
      ))}
    </div>
  )
}

function IosBottomBar({ highlight }: { highlight?: boolean }) {
  return (
    <div className="flex items-center justify-around border-t border-gray-200 bg-gray-50 px-3 py-2">
      <ChevronRight size={12} className="text-primary-600" />
      <ChevronLeft size={12} className="text-gray-300" />
      <span className={highlight ? HIGHLIGHT_ICON : undefined}>
        <Share size={13} className="text-primary-600" />
      </span>
      <BookOpen size={12} className="text-primary-600" />
      <Copy size={12} className="text-primary-600" />
    </div>
  )
}

function IosShareSheet() {
  const items = [
    { icon: <Copy size={10} />, label: 'העתקה' },
    { icon: <Star size={10} />, label: 'הוספה למועדפים' },
    { icon: <Plus size={10} />, label: 'הוספה למסך הבית', active: true },
    { icon: <Search size={10} />, label: 'חיפוש בדף' },
  ]
  return (
    <div className="absolute inset-x-2 bottom-2 z-10 overflow-hidden rounded-xl bg-white shadow-2xl">
      <div className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-2">
        <img src={APP_ICON} alt="" className="h-6 w-6 rounded" />
        <div className="min-w-0 flex-1">
          <div className="h-1.5 w-16 rounded bg-gray-300" />
          <div className="mt-1 h-1.5 w-10 rounded bg-gray-200" />
        </div>
      </div>
      {items.map((item) => (
        <div
          key={item.label}
          className={`flex items-center justify-between px-2.5 py-2 text-[9px] ${
            item.active ? `bg-primary-50 font-semibold text-primary-700 ${HIGHLIGHT}` : 'text-gray-700'
          }`}
        >
          <span>{item.label}</span>
          <span className="text-gray-400">{item.icon}</span>
        </div>
      ))}
    </div>
  )
}

/** The final confirmation dialog — "התקן" on Android, "הוסף" on iOS. */
function ConfirmDialog({ confirmLabel }: { confirmLabel: string }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 px-3">
      <div className="w-full rounded-xl bg-white p-3 shadow-2xl">
        <div className="flex items-center gap-2">
          <img src={APP_ICON} alt="" className="h-8 w-8 rounded-lg" />
          <div className="min-w-0">
            <p className="truncate text-[10px] font-semibold text-gray-900">{APP_NAME}</p>
            <p className="truncate text-[8px] text-gray-500">אזור אישי</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <span className="text-[9px] text-gray-500">ביטול</span>
          <span className={`rounded-md bg-primary-600 px-2.5 py-1 text-[9px] font-semibold text-white ${HIGHLIGHT}`}>
            {confirmLabel}
          </span>
        </div>
      </div>
    </div>
  )
}

/** Home screen with our icon sitting among the others. */
function HomeScreenGrid() {
  return (
    <div className="flex-1 bg-gradient-to-b from-sky-100 to-indigo-100 p-3">
      <div className="grid grid-cols-4 gap-x-2 gap-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="h-8 w-8 rounded-xl bg-white/70" />
            <div className="h-1 w-6 rounded bg-white/70" />
          </div>
        ))}
        <div className="space-y-1">
          <img src={APP_ICON} alt="" className={`h-8 w-8 rounded-xl shadow-md ${HIGHLIGHT}`} />
          <p className="text-[6px] font-medium leading-tight text-gray-700">{APP_NAME}</p>
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="h-8 w-8 rounded-xl bg-white/70" />
            <div className="h-1 w-6 rounded bg-white/70" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * A phone frame with one guide screen inside.
 * Decorative by design — the step text carries the meaning.
 */
export default function PhoneMockup({ screen, host }: { screen: StepScreen; host: string }) {
  const isIos = screen.startsWith('ios')

  return (
    <div aria-hidden="true" className="mx-auto w-[210px] select-none sm:w-[230px]">
      <div className="relative rounded-[2rem] bg-gray-900 p-2 shadow-2xl">
        {/* Volume / power buttons */}
        <span className="absolute -right-[3px] top-20 h-10 w-[3px] rounded-r bg-gray-700" />
        <span className="absolute -left-[3px] top-16 h-6 w-[3px] rounded-l bg-gray-700" />

        <div className="relative flex aspect-[9/17] flex-col overflow-hidden rounded-[1.5rem] bg-white">
          {/* Notch */}
          <span className="absolute left-1/2 top-0 z-20 h-3 w-14 -translate-x-1/2 rounded-b-xl bg-gray-900" />

          <StatusBar />

          {isIos ? (
            <>
              <PageContent />
              {screen === 'ios-share-sheet' && <IosShareSheet />}
              {screen === 'ios-confirm' && <ConfirmDialog confirmLabel="הוסף" />}
              <IosBottomBar highlight={screen === 'ios-share-button'} />
            </>
          ) : screen === 'home-screen' ? (
            <HomeScreenGrid />
          ) : (
            <>
              <AndroidUrlBar host={host} highlight={screen === 'android-menu-button'} />
              <PageContent />
              {screen === 'android-menu' && <AndroidMenu />}
              {screen === 'android-confirm' && <ConfirmDialog confirmLabel="התקן" />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
